import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Lock, LogOut, Loader2, Package, Mail, Phone, MapPin, RefreshCcw,
  CheckCircle2, Clock, Truck, FileText, Undo2, ArrowLeft, AlertTriangle, Download,
} from "lucide-react";
import {
  adminToken, adminLogin, adminLogout, adminListOrders,
  adminMarkShipped, adminUnmarkShipped, adminBackfillShipping,
} from "@/lib/api";

export default function Admin() {
  const [authed, setAuthed] = useState(!!adminToken.get());

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[#0a0a0a] text-[#cbc1ad]">
      {authed ? <Dashboard onLogout={() => setAuthed(false)} /> : <LoginView onAuth={() => setAuthed(true)} />}
    </main>
  );
}

/* ----------------- Login ----------------- */
function LoginView({ onAuth }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      await adminLogin(password);
      onAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-[#0d0c0b] border border-[#2a2422] p-8"
        data-testid="admin-login-form"
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#7a716a] hover:text-[#d89557] transition mb-8"
        >
          <ArrowLeft className="w-3 h-3" /> Retour site
        </Link>
        <div className="flex items-center gap-3 mb-6">
          <Lock className="w-4 h-4 text-[#d89557]" />
          <h1 className="font-display text-2xl text-[#f4ede0]">Administration</h1>
        </div>
        <p className="text-xs text-[#7a716a] uppercase tracking-[0.22em] mb-6">
          Accès réservé smartxglass
        </p>
        <label className="block text-[10px] uppercase tracking-[0.25em] text-[#cbc1ad] mb-2">
          Mot de passe
        </label>
        <input
          data-testid="admin-password"
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-[#0a0a0a] border border-[#2a2422] focus:border-[#b87333] outline-none px-4 py-3 text-sm text-[#f4ede0] transition"
          placeholder="••••••••"
        />
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading || !password}
          className="btn-copper w-full mt-6 py-3 text-xs uppercase tracking-[0.22em] inline-flex items-center justify-center gap-3 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
          Se connecter
        </button>
      </form>
    </div>
  );
}

/* ----------------- Dashboard ----------------- */
function Dashboard({ onLogout }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("to_ship"); // to_ship | shipped | all
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListOrders();
      setOrders(data);
    } catch (err) {
      if (err.response?.status === 401) {
        adminToken.clear();
        onLogout();
        return;
      }
      toast.error("Impossible de charger les commandes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleLogout = async () => {
    await adminLogout();
    onLogout();
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await adminBackfillShipping();
      toast.success(`${r.updated} / ${r.scanned} commande(s) resynchronisée(s) depuis Stripe`);
      if (r.errors?.length) {
        console.warn("Backfill errors:", r.errors);
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de synchronisation");
    } finally {
      setSyncing(false);
    }
  };

  const filtered = orders.filter((o) => {
    if (o.payment_status !== "paid") return false;
    const shipped = o.shipment?.status === "shipped";
    if (filter === "to_ship") return !shipped;
    if (filter === "shipped") return shipped;
    return true;
  });

  const toShipCount = orders.filter((o) => o.payment_status === "paid" && o.shipment?.status !== "shipped").length;
  const shippedCount = orders.filter((o) => o.payment_status === "paid" && o.shipment?.status === "shipped").length;
  const incompleteCount = orders.filter(
    (o) => o.payment_status === "paid" && (!o.shipping?.name || !o.shipping?.address?.line1)
  ).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
      {/* Header */}
      <header className="flex items-center justify-between mb-10 flex-wrap gap-4">
        <div>
          <span className="text-[10px] uppercase tracking-[0.32em] text-[#d89557]">· Back-office ·</span>
          <h1 className="font-display text-3xl sm:text-4xl text-[#f4ede0] mt-2">Commandes</h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="admin-refresh"
            onClick={load}
            className="px-4 py-2 text-xs uppercase tracking-[0.22em] border border-[#2a2422] text-[#cbc1ad] hover:border-[#b87333]/50 hover:text-[#f4ede0] transition inline-flex items-center gap-2"
          >
            <RefreshCcw className="w-3.5 h-3.5" /> Actualiser
          </button>
          <button
            data-testid="admin-logout"
            onClick={handleLogout}
            className="px-4 py-2 text-xs uppercase tracking-[0.22em] border border-[#2a2422] text-[#cbc1ad] hover:border-red-500/40 hover:text-red-400 transition inline-flex items-center gap-2"
          >
            <LogOut className="w-3.5 h-3.5" /> Déconnexion
          </button>
        </div>
      </header>

      {/* Incomplete-address banner */}
      {incompleteCount > 0 && (
        <div
          data-testid="incomplete-banner"
          className="mb-6 p-4 border border-[#b87333]/40 bg-[#b87333]/10 flex items-start gap-4 flex-wrap"
        >
          <AlertTriangle className="w-5 h-5 text-[#d89557] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-[220px]">
            <div className="text-sm text-[#f4ede0]">
              {incompleteCount} commande(s) sans adresse en base
            </div>
            <div className="text-xs text-[#cbc1ad] mt-1 leading-relaxed">
              Ces commandes ont été créées avant le fix Stripe. Les adresses existent dans Stripe — clique pour les récupérer.
            </div>
          </div>
          <button
            data-testid="sync-stripe"
            onClick={handleSync}
            disabled={syncing}
            className="btn-copper px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] inline-flex items-center gap-2 disabled:opacity-50"
          >
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Resynchroniser depuis Stripe
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-6 flex-wrap" data-testid="admin-filters">
        <FilterChip active={filter === "to_ship"} onClick={() => setFilter("to_ship")} label={`À expédier (${toShipCount})`} testid="filter-to-ship" />
        <FilterChip active={filter === "shipped"} onClick={() => setFilter("shipped")} label={`Expédiées (${shippedCount})`} testid="filter-shipped" />
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label="Toutes (payées)" testid="filter-all" />
      </div>

      {/* Orders */}
      {loading ? (
        <div className="py-20 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#d89557] mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center text-sm text-[#7a716a]">
          Aucune commande dans cette catégorie.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((o) => (
            <OrderRow key={o.session_id} order={o} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}

const FilterChip = ({ active, onClick, label, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`px-4 py-2 text-[11px] uppercase tracking-[0.22em] border transition ${
      active
        ? "border-[#b87333] text-[#d89557] bg-[#b87333]/10"
        : "border-[#2a2422] text-[#cbc1ad] hover:border-[#b87333]/40 hover:text-[#f4ede0]"
    }`}
  >
    {label}
  </button>
);

/* ----------------- Order Row ----------------- */
function OrderRow({ order, onChanged }) {
  const [open, setOpen] = useState(false);
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("La Poste suisse");
  const [sending, setSending] = useState(false);

  const isSample = order.type === "sample";
  const shipped = order.shipment?.status === "shipped";
  const ship = order.shipping || {};
  const addr = ship.address || {};
  const createdDate = order.created_at ? new Date(order.created_at).toLocaleDateString("fr-CH") : "—";

  const markShipped = async () => {
    if (!isSample && !tracking.trim()) {
      toast.error("Numéro de suivi obligatoire");
      return;
    }
    setSending(true);
    try {
      const r = await adminMarkShipped(order.session_id, {
        tracking_number: isSample ? null : tracking.trim(),
        carrier: isSample ? null : carrier,
      });
      toast.success(
        isSample
          ? r.email_sent ? "Lettre marquée envoyée · email client envoyé" : "Marquée envoyée · ⚠️ email client non envoyé"
          : r.email_sent ? "Colis marqué expédié · email client envoyé" : "Marqué expédié · ⚠️ email client non envoyé"
      );
      setOpen(false);
      setTracking("");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSending(false);
    }
  };

  const unmark = async () => {
    if (!window.confirm("Annuler l'expédition de cette commande ? (le client n'est pas renotifié)")) return;
    try {
      await adminUnmarkShipped(order.session_id);
      toast.success("Expédition annulée");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div
      data-testid={`order-row-${order.session_id}`}
      className={`border bg-[#0d0c0b] transition ${
        shipped ? "border-[#2a2422]" : "border-[#b87333]/30"
      }`}
    >
      {/* Summary line */}
      <div className="p-4 sm:p-5 grid grid-cols-12 gap-3 items-center">
        <div className="col-span-12 sm:col-span-2 text-xs">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#7a716a]">{createdDate}</div>
          <div className={`mt-1 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 border ${
            isSample ? "border-[#b87333]/40 text-[#d89557]" : "border-[#2a2422] text-[#cbc1ad]"
          }`}>
            {isSample ? <Mail className="w-3 h-3" /> : <Package className="w-3 h-3" />}
            {isSample ? "Échantillon" : "Sur-mesure"}
          </div>
        </div>

        <div className="col-span-12 sm:col-span-3">
          <div className="text-sm text-[#f4ede0] font-medium">{ship.name || "— nom manquant —"}</div>
          <div className="text-xs text-[#cbc1ad] truncate">{order.customer_email || "—"}</div>
        </div>

        <div className="col-span-12 sm:col-span-3 text-xs text-[#cbc1ad] leading-relaxed">
          {addr.line1 ? (
            <>
              <div>{addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}</div>
              <div>{addr.postal_code} {addr.city} {addr.country}</div>
            </>
          ) : <span className="text-[#7a716a]">— adresse manquante —</span>}
        </div>

        <div className="col-span-6 sm:col-span-2 text-right">
          <div className="font-display text-xl text-[#f4ede0]">{(order.amount || 0).toFixed(2)}</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#7a716a]">CHF TTC</div>
        </div>

        <div className="col-span-6 sm:col-span-2 flex sm:justify-end">
          {shipped ? (
            <div className="text-right">
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#d89557] px-2 py-1 border border-[#b87333]/40">
                <CheckCircle2 className="w-3.5 h-3.5" /> Envoyée
              </div>
              <button
                onClick={unmark}
                className="mt-2 block text-[10px] uppercase tracking-[0.2em] text-[#7a716a] hover:text-red-400 transition inline-flex items-center gap-1"
                data-testid={`unmark-${order.session_id}`}
              >
                <Undo2 className="w-3 h-3" /> Annuler
              </button>
            </div>
          ) : (
            <button
              data-testid={`mark-shipped-${order.session_id}`}
              onClick={() => setOpen((v) => !v)}
              className="btn-copper px-4 py-2.5 text-[10px] uppercase tracking-[0.22em] inline-flex items-center gap-2"
            >
              {isSample ? <Mail className="w-3.5 h-3.5" /> : <Truck className="w-3.5 h-3.5" />}
              {isSample ? "Lettre envoyée" : "Colis expédié"}
            </button>
          )}
        </div>
      </div>

      {/* Expand panel */}
      {open && !shipped && (
        <div className="p-5 border-t border-[#2a2422] bg-[#0a0a0a] space-y-4">
          {/* Full details */}
          <div className="grid sm:grid-cols-2 gap-4 text-xs">
            <DetailRow icon={<Phone className="w-3.5 h-3.5" />} label="Téléphone" value={ship.phone || order.phone || "—"} />
            <DetailRow icon={<Mail className="w-3.5 h-3.5" />} label="E-mail" value={order.customer_email || "—"} />
            <DetailRow
              icon={<MapPin className="w-3.5 h-3.5" />}
              label="Adresse complète"
              value={
                addr.line1
                  ? `${ship.name || ""}\n${addr.line1}${addr.line2 ? "\n" + addr.line2 : ""}\n${addr.postal_code || ""} ${addr.city || ""}\n${addr.country || ""}`.trim()
                  : "—"
              }
              multiline
            />
            <DetailRow icon={<FileText className="w-3.5 h-3.5" />} label="Référence" value={order.id || order.session_id} />
            {order.breakdown?.total_m2 != null && (
              <DetailRow label="Surface" value={`${order.breakdown.total_m2} m²`} />
            )}
          </div>

          {/* Tracking input for custom orders */}
          {!isSample && (
            <div className="grid sm:grid-cols-3 gap-3 pt-3 border-t border-[#2a2422]">
              <div className="sm:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.22em] text-[#d89557] mb-2">
                  Numéro de suivi *
                </label>
                <input
                  data-testid={`tracking-input-${order.session_id}`}
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value)}
                  placeholder="ex: 99.00.000000.00000000"
                  className="w-full bg-[#0d0c0b] border border-[#2a2422] focus:border-[#b87333] outline-none px-3 py-2.5 text-sm text-[#f4ede0] font-mono"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.22em] text-[#d89557] mb-2">
                  Transporteur
                </label>
                <select
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  className="w-full bg-[#0d0c0b] border border-[#2a2422] focus:border-[#b87333] outline-none px-3 py-2.5 text-sm text-[#f4ede0]"
                >
                  <option>La Poste suisse</option>
                  <option>DHL</option>
                  <option>DPD</option>
                  <option>Autre</option>
                </select>
              </div>
            </div>
          )}

          {isSample && (
            <div className="text-xs text-[#7a716a] p-3 border border-[#2a2422] bg-[#0d0c0b]">
              📬 Envoi par <strong className="text-[#cbc1ad]">lettre simple</strong> (sans suivi). Le client sera notifié par email que son échantillon est en route.
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setOpen(false)}
              className="px-4 py-2.5 text-[10px] uppercase tracking-[0.22em] border border-[#2a2422] text-[#cbc1ad] hover:text-[#f4ede0] transition"
            >
              Annuler
            </button>
            <button
              data-testid={`confirm-shipped-${order.session_id}`}
              onClick={markShipped}
              disabled={sending || (!isSample && !tracking.trim())}
              className="btn-copper px-5 py-2.5 text-[10px] uppercase tracking-[0.22em] inline-flex items-center gap-2 disabled:opacity-50"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Confirmer et notifier
            </button>
          </div>
        </div>
      )}

      {/* Already shipped info */}
      {shipped && (
        <div className="px-5 py-3 border-t border-[#2a2422] bg-[#0a0a0a] text-xs text-[#7a716a] flex items-center gap-4 flex-wrap">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3 h-3" />
            Expédiée le {new Date(order.shipment.shipped_at).toLocaleString("fr-CH")}
          </span>
          {order.shipment.tracking_number && (
            <span className="inline-flex items-center gap-1.5">
              <Package className="w-3 h-3" />
              {order.shipment.carrier || "Suivi"} · <span className="font-mono text-[#cbc1ad]">{order.shipment.tracking_number}</span>
            </span>
          )}
          <span className={`inline-flex items-center gap-1.5 ${order.shipment.email_sent ? "text-[#d89557]" : "text-red-400"}`}>
            <Mail className="w-3 h-3" />
            {order.shipment.email_sent ? "Email client envoyé" : "⚠️ Email non envoyé"}
          </span>
        </div>
      )}
    </div>
  );
}

const DetailRow = ({ icon, label, value, multiline }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.22em] text-[#7a716a] mb-1 inline-flex items-center gap-1.5">
      {icon}{label}
    </div>
    <div className={`text-[#f4ede0] ${multiline ? "whitespace-pre-line text-sm leading-relaxed" : "text-sm"}`}>
      {value}
    </div>
  </div>
);
